import { Dialog, DialogContent, Tooltip } from '@mui/material';
import React from 'react';
import styles from './subscriptionDialog.module.css';
import { MdCheck, MdClose, MdInfo } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useSubscriptionLogic } from 'pages/PhotoAdmin/AdminMenu/Subscription/useSubscriptionLogic';
import ConfirmDialog from 'components/common/ConfirmDialog/ConfirmDialog';
import { useState } from 'react';
import aszf from 'assets/files/aszf_DT_fotos.pdf';
import { useTranslation } from 'react-i18next';
const SubscriptionDialog = ({ bundleData, assign, unassign, open, setOpen, isAnnual }) => {
  const shopTier = useSelector((state) => state.nonPersistedReducers?.shopTierSliceReducer?.shopTier);
  const { t } = useTranslation();
  const { fetchCardRegister } = useSubscriptionLogic();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const user = useSelector((state) => state.nonPersistedReducers?.userSliceReducer?.currentUser);
  const billing_date =
    user?.ownedShops[0]?.paymentData?.length > 0 &&
    user?.ownedShops[0]?.paymentData[user?.ownedShops[0]?.paymentData?.length - 1]?.dueDate?.substring(0, 10);

  const handleSubmit = async () => {
    shopTier?.id !== bundleData?.id
      ? assign.mutate({
          tierId: bundleData?.id,
          isAnnual: bundleData?.recurringPrice === 0 ? false : isAnnual,
          setOpen: setOpen
        })
      : assign.mutate({ tierId: 1, isAnnual: false, setOpen: setOpen });
  };

  const renderActionButton = () => {
    const isFreePlanWithNoCommission =
      bundleData?.recurringPrice === 0 && bundleData?.commissionRate === 0 && bundleData?.id === shopTier?.id;

    const hasValidToken = fetchCardRegister?.data?.data?.tokens?.find(
      (token) => token?.isValid === true && token?.used === false
    );

    const buttonText =
      shopTier?.id !== bundleData?.id
        ? t('subscription.bundles.changeSubscription')
        : t('subscription.bundles.unsubscribe');

    const commonButtonProps = {
      disabled: assign?.isLoading || unassign?.isLoading,
      className: styles.submit_button,
      onClick: () => setConfirmOpen(true)
    };

    // Case 1: Free plan with no commission - render nothing
    if (isFreePlanWithNoCommission) {
      return null;
    }

    // Case 2: No valid card but free or professional
    if (!hasValidToken && bundleData?.recurringPrice === 0) {
      return <button {...commonButtonProps}>{buttonText}</button>;
    }

    // Case 3: Has valid token or is current tier
    if (hasValidToken || shopTier?.id === bundleData?.id) {
      return <button {...commonButtonProps}>{buttonText}</button>;
    }

    // Case 4: Free or professional current plan but different bundle with 0 recurringPrice with no valid token
    if (
      shopTier?.recurringPrice === 0 &&
      bundleData?.id !== shopTier?.id &&
      bundleData?.recurringPrice === 0 &&
      !hasValidToken
    ) {
      return <button {...commonButtonProps}>{buttonText}</button>;
    }

    // Default case: Disabled button with tooltip
    return (
      <Tooltip title={t('subscription.bundles.registerCard')}>
        <button className={styles.disabled_button} onClick={() => {}}>
          {buttonText}
        </button>
      </Tooltip>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)}>
        {shopTier?.id === bundleData?.id && (
          <div className={styles.dialog_banner}>{t('subscription.bundles.current')}</div>
        )}
        <DialogContent>
          <div className={styles.dialog_content}>
            <h2>{t(`subscription.bundles.${bundleData?.name}`)}</h2>
            {shopTier?.id !== bundleData?.id && <p>{t('subscription.bundles.dialogSubtitle')}</p>}
            <div className={styles.tier_info_wrapper}>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.maximumGallery')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.maximumGalleryMain')}
                  </p>
                </Tooltip>
                <p>{bundleData?.galleryLimit}db</p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.maximumUpload')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.uploadLimit')}
                  </p>
                </Tooltip>
                <p>{bundleData?.productsLimit}db</p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.newGallery')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.createGallery')}
                  </p>
                </Tooltip>
                <p>
                  <MdCheck color='green' fontSize={22} />
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.imageUpload')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.imageUploadMain')}
                  </p>
                </Tooltip>
                <p>
                  <MdCheck color='green' fontSize={22} />
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.categories')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.ownProduct')}
                  </p>
                </Tooltip>
                <p>
                  <MdCheck color='green' fontSize={22} />
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.reports')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.reportsMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.reports ? (
                    <MdCheck
                      color={
                        bundleData?.reports !== shopTier?.reports && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.reports !== shopTier?.reports && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.printing')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.printingMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.printOption ? (
                    <MdCheck
                      color={
                        bundleData?.printOption !== shopTier?.printOption && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.printOption !== shopTier?.printOption && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.orderDeadline')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.orderDeadlineMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.galleryExpiryDate ? (
                    <MdCheck
                      color={
                        bundleData?.galleryExpiryDate !== shopTier?.galleryExpiryDate && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.galleryExpiryDate !== shopTier?.galleryExpiryDate && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.inheritDate')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.inheritDateMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.expiryDateInheritance ? (
                    <MdCheck
                      color={
                        bundleData?.expiryDateInheritance !== shopTier?.expiryDateInheritance && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.expiryDateInheritance !== shopTier?.expiryDateInheritance && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.inheritPrice')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.inheritPriceMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.priceInheritance ? (
                    <MdCheck
                      color={
                        bundleData?.priceInheritance !== shopTier?.priceInheritance && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.priceInheritance !== shopTier?.priceInheritance && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.generateGroup')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.generateGroupMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.groupGallery ? (
                    <MdCheck
                      color={
                        bundleData?.groupGallery !== shopTier?.groupGallery && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.groupGallery !== shopTier?.groupGallery && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.galleryCode')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.galleryCodeMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.galleryCoding ? (
                    <MdCheck
                      color={
                        bundleData?.galleryCoding !== shopTier?.galleryCoding && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.galleryCoding !== shopTier?.galleryCoding && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.onlinePayment')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.onlinePaymentMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.onlinePayment ? (
                    <MdCheck
                      color={
                        bundleData?.onlinePayment !== shopTier?.onlinePayment && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.onlinePayment !== shopTier?.onlinePayment && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.statusAutomation')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.statusAutomationMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.statusAutomation ? (
                    <MdCheck
                      color={
                        bundleData?.statusAutomation !== shopTier?.statusAutomation && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.statusAutomation !== shopTier?.statusAutomation && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.sendDigital')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.sendDigitalMain')}
                  </p>
                </Tooltip>
                <p>
                  {bundleData?.digitalImageSending ? (
                    <MdCheck
                      color={
                        bundleData?.digitalImageSending !== shopTier?.digitalImageSending && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.digitalImageSending !== shopTier?.digitalImageSending && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.pricingTemplate')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.pricingTemplateMain')}
                  </p>
                </Tooltip>{' '}
                <p>
                  {bundleData?.pricingTemplates ? (
                    <MdCheck
                      color={
                        bundleData?.pricingTemplates !== shopTier?.pricingTemplates && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.pricingTemplates !== shopTier?.pricingTemplates && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
              <div className={styles.detail_item}>
                <Tooltip title={t('subscription.bundles.automaticInvoice')}>
                  <p>
                    <MdInfo /> {t('subscription.bundles.automaticInvoiceMain')}
                  </p>
                </Tooltip>{' '}
                <p>
                  {bundleData?.automaticInvoice ? (
                    <MdCheck
                      color={
                        bundleData?.automaticInvoice !== shopTier?.automaticInvoice && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'green'
                      }
                      fontSize={22}
                    />
                  ) : (
                    <MdClose
                      color={
                        bundleData?.automaticInvoice !== shopTier?.automaticInvoice && !!shopTier
                          ? 'var(--primary-background-secondary)'
                          : 'red'
                      }
                      fontSize={22}
                    />
                  )}
                </p>
              </div>
            </div>
            <div className={styles.price_wrapper}>
              <b>{t('subscription.bundles.bundlePrice')}:</b>{' '}
              {bundleData.recurringPrice > 0 ? (
                isAnnual ? (
                  <div className={styles.payment}>
                    {bundleData.recurringPriceYearly} {t('subscription.bundles.yearlyPrice')}
                  </div>
                ) : (
                  <div className={styles.payment}>
                    {bundleData.recurringPrice} {t('subscription.bundles.monthlyPrice')}
                  </div>
                )
              ) : bundleData.commissionRate > 0 ? (
                <div className={styles.payment}>
                  {t('subscription.bundles.commissionRate')} {bundleData.commissionRate * 100} %
                </div>
              ) : isAnnual ? (
                <div className={styles.payment}>0 {t('subscription.bundles.yearlyPrice')}</div>
              ) : (
                <div className={styles.payment}>0 {t('subscription.bundles.monthlyPrice')}</div>
              )}
            </div>
            {shopTier?.id !== bundleData?.id ? (
              isAnnual &&
              bundleData?.recurringPrice > 0 &&
              bundleData?.recurringPrice > shopTier?.recurringPrice &&
              shopTier?.recurringPrice > 0 ? (
                <p className={styles.info_message}>
                  {t('subscription.bundles.toNewBundle')} <b>{t('subscription.bundles.instantChange')}</b>
                  <br />
                  <br />
                  <b>{t('subscription.bundles.newBundle1')}</b>
                  {t('subscription.bundles.newBundle2')}
                  <br />
                  <br /> {t('subscription.bundles.doYouAccept')}
                  <b>{t('subscription.bundles.newBundle3')}</b>
                </p>
              ) : shopTier?.galleryLimit > bundleData?.galleryLimit && shopTier?.recurringPrice > 0 ? (
                <p className={styles.info_message}>
                  {t('subscription.bundles.newPricing1')}
                  <b>
                    {t('subscription.bundles.newPricing2')}
                    {billing_date ? `, ${billing_date} dátummal` : ''}
                    {t('subscription.bundles.newPricing3')}
                  </b>
                  <br />
                  <br />
                  {t('subscription.bundles.newPricing4')}
                  <br />
                  <br />
                  <b>{t('subscription.bundles.attention')}</b>
                  {t('subscription.bundles.newPricing5')}
                  <br />
                  <br /> {t('subscription.bundles.doYouAccept')}
                  <b>{t('subscription.bundles.newPricing6')}</b>
                </p>
              ) : bundleData?.recurringPrice === 0 && bundleData?.commissionRate > 0 ? (
                <p className={styles.info_message}>
                  {t('subscription.bundles.toNewBundle')} <b>{t('subscription.bundles.instantChange')}</b> <br />
                  <br />
                  {t('subscription.bundles.newInstant1')}
                  <br />
                  <br />
                  {t('subscription.bundles.newInstant2')}
                  <b>
                    {t('subscription.bundles.newInstant3')}
                    <a href={aszf} target='_blank' rel='noopener noreferrer'>
                      {t('subscription.bundles.newInstant4')}
                    </a>{' '}
                    {t('subscription.bundles.newInstant5')}
                  </b>
                  <br />
                  <br /> {t('subscription.bundles.doYouAccept')}
                  <b>{t('subscription.bundles.newInstant6')}</b>
                </p>
              ) : shopTier?.recurringPrice === 0 && shopTier?.commissionRate > 0 ? (
                <p className={styles.info_message}>
                  {t('subscription.bundles.toNewBundle')} <b>{t('subscription.bundles.instantChange')}</b> <br />
                  <br />
                  {t('subscription.bundles.news1')}
                  <br />
                  <br /> {t('subscription.bundles.doYouAccept')}
                  <b>{t('subscription.bundles.news2')}</b>
                </p>
              ) : (
                <p className={styles.info_message}>
                  {t('subscription.bundles.toNewBundle')}
                  <b>{t('subscription.bundles.instantChange')}</b> <br />
                  <br />
                  {t('subscription.bundles.others1')}
                  <br />
                  <br /> {t('subscription.bundles.doYouAccept')}
                  <b>{t('subscription.bundles.others2')}</b>
                </p>
              )
            ) : bundleData?.recurringPrice === 0 &&
              bundleData?.commissionRate === 0 &&
              bundleData?.id === shopTier?.id ? (
              <></>
            ) : (shopTier?.recurringPrice === 0 && shopTier?.commissionRate > 0) ||
              (shopTier?.id === bundleData?.id && shopTier?.commissionRate > 0) ? (
              <p className={styles.info_message}>
                {t('subscription.bundles.unsubscribe1')}
                <b>{t('subscription.bundles.unsubscribe2')}</b> <br />
                <br />
                {t('subscription.bundles.unsubscribe3')}
                <br />
                <br />
                <b>{t('subscription.bundles.attention')}</b>
                {t('subscription.bundles.unsubscribe4')}
              </p>
            ) : (
              <p className={styles.info_message}>
                {t('subscription.bundles.current1')}
                <b>
                  {t('subscription.bundles.current2')}
                  {billing_date ? `, ${billing_date} dátummal` : ''}
                  {t('subscription.bundles.current3')}
                </b>
                <br />
                <br />
                {t('subscription.bundles.current4')}
                <br />
                <br />
                <b>{t('subscription.bundles.attention')}</b>
                {t('subscription.bundles.current5')}
              </p>
            )}

            <div className={styles.button_wrapper}>
              <button className={styles.cancel_button} onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </button>
              {renderActionButton()}
            </div>
          </div>
        </DialogContent>
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          functionButtonText={t('common.yes')}
          closeButtonText={t('common.cancel')}
          multipleButtons
          title={
            shopTier?.id !== bundleData?.id
              ? t('subscription.bundles.sureChangeBundle')
              : t('subscription.bundles.sureUnsubscribe')
          }
          onOk={() => {
            handleSubmit();
            setConfirmOpen(false);
          }}
        />
      </Dialog>
    </>
  );
};

export default SubscriptionDialog;
